import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { personasAPI } from "@/lib/api";
import { ArrowLeft, Users, TreePine, Info, ChevronDown, ChevronRight, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

// Componente para mostrar una tarjeta de persona
const PersonaCard = ({ persona, onClick, isMain = false, size = "normal" }) => {
  if (!persona) return null;
  
  const sizeClasses = size === "small" 
    ? "p-2 min-w-[120px]" 
    : "p-3 min-w-[180px]";
  
  return (
    <div 
      onClick={() => onClick && onClick(persona)}
      className={`
        ${sizeClasses}
        rounded-lg bg-white shadow-md border-2 cursor-pointer
        hover:shadow-lg transition-all duration-200
        ${isMain ? 'border-emerald-500 ring-2 ring-emerald-200' : 'border-stone-200'}
        ${persona.genero === 'masculino' ? 'border-l-4 border-l-blue-500' : 'border-l-4 border-l-pink-500'}
      `}
    >
      <div className="flex items-center gap-2">
        <span className="text-xl">{persona.genero === 'masculino' ? '👨' : '👩'}</span>
        <div className="flex-1 overflow-hidden">
          <p className={`font-semibold text-stone-800 truncate ${size === 'small' ? 'text-xs' : 'text-sm'}`}>
            {persona.nombre}
          </p>
          <p className={`text-stone-500 truncate ${size === 'small' ? 'text-xs' : 'text-xs'}`}>
            {persona.apellido}
          </p>
          {persona.fecha_nacimiento && size !== "small" && (
            <p className="text-xs text-stone-400">{persona.fecha_nacimiento}</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Componente para mostrar la pareja
const ParejaSection = ({ persona1, persona2, onClick }) => {
  return (
    <div className="flex items-center gap-2 justify-center">
      <PersonaCard persona={persona1} onClick={onClick} isMain />
      {persona2 && (
        <>
          <div className="text-lg text-rose-600">⚭</div>
          <PersonaCard persona={persona2} onClick={onClick} />
        </>
      )}
    </div>
  );
};

// Líneas de conexión SVG
const ConnectionLine = ({ type }) => {
  if (type === "vertical") {
    return (
      <div className="flex justify-center">
        <div className="w-0.5 h-8 bg-stone-300" />
      </div>
    );
  }
  return null;
};

export default function ArbolGenealogico() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [personas, setPersonas] = useState([]);
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [loading, setLoading] = useState(true);
  const [familyTree, setFamilyTree] = useState(null);

  // Cargar todas las personas
  useEffect(() => {
    loadPersonas();
  }, []);

  const loadPersonas = async () => {
    try {
      setLoading(true);
      const response = await personasAPI.getAll();
      setPersonas(response.data);
      
      // Si hay un ID en la URL, seleccionar esa persona
      if (id) {
        const persona = response.data.find(p => p.id === id);
        if (persona) {
          setSelectedPersona(persona);
          buildFamilyTree(persona, response.data);
        }
      }
    } catch (error) {
      toast.error("Error al cargar las personas");
    } finally {
      setLoading(false);
    }
  };

  // Construir el árbol familiar para una persona
  const buildFamilyTree = (persona, allPersonas) => {
    if (!persona) return;
    
    const findById = (id) => allPersonas.find(p => p.id === id);
    
    // Encontrar padre y madre
    const padre = findById(persona.padre_id);
    const madre = findById(persona.madre_id);
    
    // Encontrar cónyuge
    const conyuge = findById(persona.conyuge_id);
    
    // Encontrar hijos
    const hijos = persona.hijos_ids?.map(id => findById(id)).filter(Boolean) || [];
    
    // Encontrar abuelos paternos
    const abueloPaterno = padre ? findById(padre.padre_id) : null;
    const abuelaPaterna = padre ? findById(padre.madre_id) : null;
    
    // Encontrar abuelos maternos
    const abueloMaterno = madre ? findById(madre.padre_id) : null;
    const abuelaMaterna = madre ? findById(madre.madre_id) : null;
    
    // Encontrar hermanos (otros hijos de los mismos padres)
    const hermanos = allPersonas.filter(p => 
      p.id !== persona.id && 
      ((persona.padre_id && p.padre_id === persona.padre_id) || 
       (persona.madre_id && p.madre_id === persona.madre_id))
    );
    
    setFamilyTree({
      persona,
      padre,
      madre,
      conyuge,
      hijos,
      abueloPaterno,
      abuelaPaterna,
      abueloMaterno,
      abuelaMaterna,
      hermanos
    });
  };

  // Manejar selección de persona
  const handlePersonaSelect = (personaId) => {
    const persona = personas.find(p => p.id === personaId);
    if (persona) {
      setSelectedPersona(persona);
      buildFamilyTree(persona, personas);
      navigate(`/arbol-genealogico/${personaId}`, { replace: true });
    }
  };

  // Click en una tarjeta del árbol
  const handleCardClick = (persona) => {
    if (persona && persona.id) {
      handlePersonaSelect(persona.id);
    }
  };

  // Filtrar personas con relaciones familiares
  const personasConFamilia = personas.filter(p => 
    p.padre_id || p.madre_id || p.conyuge_id || p.hijos_ids?.length > 0
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <TreePine className="w-12 h-12 mx-auto mb-4 text-emerald-600 animate-pulse" />
          <p className="text-stone-500">Cargando árbol genealógico...</p>
        </div>
      </div>
    );
  }

  if (personasConFamilia.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link to="/personas">
            <Button variant="ghost">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver a Personas
            </Button>
          </Link>
        </div>
        
        <Card className="border-stone-200 bg-white">
          <CardContent className="py-12 text-center">
            <TreePine className="w-16 h-16 mx-auto mb-4 text-stone-300" />
            <h3 className="text-lg font-medium text-stone-700 mb-2">
              No hay datos familiares disponibles
            </h3>
            <p className="text-stone-500 mb-4">
              Para visualizar el árbol genealógico, ejecute el script de datos de prueba 
              que incluye relaciones familiares.
            </p>
            <Link to="/personas">
              <Button className="bg-rose-950 hover:bg-rose-900">
                <Users className="w-4 h-4 mr-2" />
                Ir a Personas
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-serif text-rose-950 tracking-tight flex items-center gap-3">
            <TreePine className="w-8 h-8 text-emerald-600" strokeWidth={1.5} />
            Árbol Genealógico
          </h1>
          <p className="text-stone-600 mt-1">
            Visualización de relaciones familiares de los feligreses
          </p>
        </div>
      </div>

      {/* Selector de persona */}
      <Card className="border-stone-200 bg-white">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className="text-sm text-stone-500 mb-1 block">Seleccionar Persona</label>
              <Select 
                value={selectedPersona?.id || ""} 
                onValueChange={handlePersonaSelect}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Seleccione una persona..." />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-[300px]">
                  {personasConFamilia.map((persona) => (
                    <SelectItem key={persona.id} value={persona.id}>
                      {persona.nombre} {persona.apellido}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Árbol Familiar */}
      {familyTree ? (
        <Card className="border-stone-200 bg-white overflow-hidden">
          <CardHeader className="border-b border-stone-100">
            <CardTitle className="text-lg font-medium text-stone-800 flex items-center gap-2">
              <Info className="w-5 h-5 text-emerald-600" />
              Familia de {familyTree.persona.nombre} {familyTree.persona.apellido}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-8">
              
              {/* GENERACIÓN 1: Abuelos */}
              {(familyTree.abueloPaterno || familyTree.abuelaPaterna || 
                familyTree.abueloMaterno || familyTree.abuelaMaterna) && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-stone-500 text-center">👴👵 Abuelos</h3>
                  <div className="flex flex-wrap justify-center gap-8">
                    {/* Abuelos paternos */}
                    {(familyTree.abueloPaterno || familyTree.abuelaPaterna) && (
                      <div className="text-center">
                        <p className="text-xs text-stone-400 mb-2">Paternos</p>
                        <div className="flex items-center gap-2">
                          {familyTree.abueloPaterno && (
                            <PersonaCard 
                              persona={familyTree.abueloPaterno} 
                              onClick={handleCardClick}
                              size="small"
                            />
                          )}
                          {familyTree.abueloPaterno && familyTree.abuelaPaterna && (
                            <span className="text-rose-500">⚭</span>
                          )}
                          {familyTree.abuelaPaterna && (
                            <PersonaCard 
                              persona={familyTree.abuelaPaterna} 
                              onClick={handleCardClick}
                              size="small"
                            />
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Abuelos maternos */}
                    {(familyTree.abueloMaterno || familyTree.abuelaMaterna) && (
                      <div className="text-center">
                        <p className="text-xs text-stone-400 mb-2">Maternos</p>
                        <div className="flex items-center gap-2">
                          {familyTree.abueloMaterno && (
                            <PersonaCard 
                              persona={familyTree.abueloMaterno} 
                              onClick={handleCardClick}
                              size="small"
                            />
                          )}
                          {familyTree.abueloMaterno && familyTree.abuelaMaterna && (
                            <span className="text-rose-500">⚭</span>
                          )}
                          {familyTree.abuelaMaterna && (
                            <PersonaCard 
                              persona={familyTree.abuelaMaterna} 
                              onClick={handleCardClick}
                              size="small"
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <ConnectionLine type="vertical" />
                </div>
              )}
              
              {/* GENERACIÓN 2: Padres */}
              {(familyTree.padre || familyTree.madre) && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-stone-500 text-center">👨👩 Padres</h3>
                  <div className="flex items-center justify-center gap-3">
                    {familyTree.padre && (
                      <PersonaCard 
                        persona={familyTree.padre} 
                        onClick={handleCardClick}
                      />
                    )}
                    {familyTree.padre && familyTree.madre && (
                      <span className="text-xl text-rose-500">⚭</span>
                    )}
                    {familyTree.madre && (
                      <PersonaCard 
                        persona={familyTree.madre} 
                        onClick={handleCardClick}
                      />
                    )}
                  </div>
                  <ConnectionLine type="vertical" />
                </div>
              )}
              
              {/* PERSONA PRINCIPAL + Cónyuge */}
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-emerald-600 text-center">⭐ Persona Seleccionada</h3>
                <div className="flex items-center justify-center gap-3">
                  <PersonaCard 
                    persona={familyTree.persona} 
                    isMain={true}
                  />
                  {familyTree.conyuge && (
                    <>
                      <span className="text-xl text-rose-500">⚭</span>
                      <PersonaCard 
                        persona={familyTree.conyuge} 
                        onClick={handleCardClick}
                      />
                    </>
                  )}
                </div>
                
                {/* Hermanos */}
                {familyTree.hermanos?.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-dashed border-stone-200">
                    <p className="text-xs text-stone-400 text-center mb-2">Hermanos</p>
                    <div className="flex flex-wrap justify-center gap-2">
                      {familyTree.hermanos.map(hermano => (
                        <PersonaCard 
                          key={hermano.id}
                          persona={hermano} 
                          onClick={handleCardClick}
                          size="small"
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* GENERACIÓN 4: Hijos */}
              {familyTree.hijos?.length > 0 && (
                <div className="space-y-2">
                  <ConnectionLine type="vertical" />
                  <h3 className="text-sm font-semibold text-stone-500 text-center">👦👧 Hijos</h3>
                  <div className="flex flex-wrap justify-center gap-3">
                    {familyTree.hijos.map(hijo => (
                      <PersonaCard 
                        key={hijo.id}
                        persona={hijo} 
                        onClick={handleCardClick}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-stone-200 bg-white">
          <CardContent className="py-12 text-center">
            <Info className="w-12 h-12 mx-auto mb-4 text-stone-300" />
            <p className="text-stone-500">
              Seleccione una persona para ver su árbol familiar
            </p>
          </CardContent>
        </Card>
      )}

      {/* Información detallada */}
      {selectedPersona && (
        <Card className="border-stone-200 bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-stone-800">
              Información de {selectedPersona.nombre} {selectedPersona.apellido}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-stone-500">Fecha de Nacimiento</p>
                <p className="font-medium">{selectedPersona.fecha_nacimiento || "No registrada"}</p>
              </div>
              <div>
                <p className="text-sm text-stone-500">Lugar de Nacimiento</p>
                <p className="font-medium">{selectedPersona.lugar_nacimiento || "No registrado"}</p>
              </div>
              <div>
                <p className="text-sm text-stone-500">Cédula</p>
                <p className="font-medium">{selectedPersona.cedula || "No registrada"}</p>
              </div>
              <div>
                <p className="text-sm text-stone-500">Teléfono</p>
                <p className="font-medium">{selectedPersona.telefono || "No registrado"}</p>
              </div>
              <div>
                <p className="text-sm text-stone-500">Email</p>
                <p className="font-medium">{selectedPersona.email || "No registrado"}</p>
              </div>
              <div>
                <p className="text-sm text-stone-500">Dirección</p>
                <p className="font-medium">{selectedPersona.direccion || "No registrada"}</p>
              </div>
            </div>
            
            <div className="mt-4 pt-4 border-t border-stone-100">
              <Link to={`/personas/${selectedPersona.id}/editar`}>
                <Button variant="outline" size="sm">
                  <User className="w-4 h-4 mr-2" />
                  Ver/Editar Perfil Completo
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
